import './App.css';
import * as React from 'react';
import { useEffect, useLayoutEffect } from 'react';

/** Material UI Packages. */
import { LicenseInfo } from '@mui/x-license-pro';

/** Router packages. */
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** HTTP Client Liabrary. */
import axios from 'axios';

/** JWT Decoder */
import { jwtDecode } from 'jwt-decode';

/** Internal Components */
import { callAPI, callAPIOP } from './api/useApiRequest';
import RenderComponent from './theme/RenderComponent';
import LoginPage from './form/LoginPage';
import ErrorPage from './form/ErrorPage';
import InactivePage from './form/InactivePage';
import useUserRole from './utils/useUserRole';

/** Load secrete keys */
const muiKey = process.env.REACT_APP_MUI_KEY;
const auth0Key = process.env.REACT_APP_AUTH0_AUTHORIZATION_API;
const awsAPIOP = process.env.REACT_APP_DEFAULT_API_OP;

/** Mui Premium license key. */
LicenseInfo.setLicenseKey(muiKey);

function App() {
    /** Destructuring values from useAuth0 hook. */
    const { getAccessTokenSilently, logout, isAuthenticated, user } = useAuth0();

    /** Get user role from useUserRole hook. */
    const userRole = useUserRole();

    /**
     * Get token value from Auth0.
     * Fetch user information via a GET request to the API endpoint.
     **/
    const getUser = async () => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: auth0Key,
                },
            });
            // Decode token and check expiration
            let decodedToken;
            try {
                decodedToken = jwtDecode(token);
            } catch (decodeError) {
                logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                    },
                });
                return;
            }

            const tokenExpiry = decodedToken.exp * 1000;
            const tokenExpiryTime = new Date(tokenExpiry);

            const resp = await axios.get(awsAPIOP, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    type: 'sign',
                    email: user.email,
                    first: user.given_name,
                    last: user.family_name,
                    role: userRole ? JSON.stringify(userRole) : null,
                    component: '',
                },
            });
            sessionStorage.setItem('exp', tokenExpiryTime);
            return resp;
        } catch (e) {
            console.log(e);
            if (e.error === 'missing_refresh_token' || e.error === 'invalid_grant') {
                console.log(e);
                logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                    },
                });
            }
        }
    };

    /** Hook to update userRole to the database. */
    useEffect(() => {
        if (isAuthenticated && user) {
            getUser()
                .then((response) => {
                    callAPIOP({
                        method: 'post',
                        module: 'signup',
                        component: '',
                        getAccessTokenSilently,
                        email: user.email,
                        obj: JSON.stringify(userRole),
                        onSuccess: (res) => {
                            console.log('User data has been successfully updated.');
                            callAPI({
                                method: 'put',
                                module: 'customers',
                                component: 'sync_all',
                                getAccessTokenSilently,
                                email: user.email,
                                onSuccess: (res) => {
                                    console.log('Full Sync Triggered:', res);
                                },
                                onError: (err) => {
                                    console.error('Full Sync Trigger error:', err);
                                },
                            });
                        },
                        onError: (err) => {
                            console.error('UserRole update error:', err);
                        },
                    });
                })
                .catch((error) => {
                    console.log('User data update has been failed.');
                });
            return;
        }
    }, [isAuthenticated, user]);

    /** Set document title. */
    useLayoutEffect(() => {
        document.title = 'Management System | Insight Logistics ';
    }, []);

    return (
        <Router>
            <Routes>
                <Route path="/" element={isAuthenticated ? <RenderComponent dashboard={true} /> : <LoginPage />} />
                <Route
                    path="/dashboard"
                    element={isAuthenticated ? <RenderComponent dashboard={true} /> : <LoginPage />}
                />
                <Route
                    path="/accountsetting"
                    element={isAuthenticated ? <RenderComponent accountsetting={true} /> : <LoginPage />}
                />
                <Route path="/account" element={isAuthenticated ? <RenderComponent account={true} /> : <LoginPage />} />
                <Route path="/addons" element={isAuthenticated ? <RenderComponent addons={true} /> : <LoginPage />} />
                <Route
                    path="/myprofile"
                    element={isAuthenticated ? <RenderComponent myprofile={true} /> : <LoginPage />}
                />
                <Route path="/inactive" element={<InactivePage />} />
                <Route path="*" element={<ErrorPage />} />
            </Routes>
        </Router>
    );
}

export default App;
